import React, { useEffect, useState, useRef, useCallback } from 'react'
import {
    getBrandHub,
    getBusinessNotifications,
} from "../services/BusinessService";
import { getCampaigns, createCampaignCreator, archiveCampaign } from "../services/CampaignService";
import { useNavigate } from "react-router-dom";
import "../stylesheets/BrandHub.css";
import Modal from "react-modal";
import CampaignPreview from './CampaignPreview';
import Header from './Header';
import Button from './Button';
import BusinessInfoCard from './BusinessInfoCard';
import NotificationsList from './NotificationsList';
import CreatorSpotlight from './CreatorSpotlight';
import CampaignHub from './CampaignHub';
import ArchiveModal from './ArchiveModal';
import { View, StyleSheet, Text, ScrollView } from 'react-native-web';

const BrandHub = () => {
    const [businessData, setBusinessData] = useState({})
    const [notifications, setNotifications] = useState([])
    const [notificationsLoading, setNotificationsLoading] = useState(false)
    const [addingCreator, setAddingCreator] = useState({})
    const [campaigns, setCampaigns] = useState([]);
    const [campaignsLoading, setCampaignsLoading] = useState(false)
    const [addToCampaignModal, setAddToCampaignModal] = useState(false)
    const [showReviewAnnouncement, setShowReviewAnnouncement] = useState(false)
    const [archiveModal, setArchiveModal] = useState(false)
    const [selectedCampaign, setSelectedCampaign] = useState({})
    const campaignAddRef = useRef(null);
    const navigate = useNavigate();


    const openAddToCampaignModal = useCallback((creator) => {
        setAddingCreator(creator);
        setAddToCampaignModal(true);
    }, []);

    const closeModal = useCallback(() => {
        setAddToCampaignModal(false);
        setAddingCreator({});
    }, []);
    
    const closeReviewAnnouncement = useCallback(() => {
        setShowReviewAnnouncement(false);
        // Store in localStorage to not show again for this session
        localStorage.setItem('reviewAnnouncementSeen', 'true');
    }, []);

    const goToCampaigns = useCallback(() => {
        closeReviewAnnouncement();
        navigate('/outreach');
    }, [closeReviewAnnouncement, navigate]);

    const handleBusinessUpdate = useCallback((updatedBusiness) => {
        setBusinessData(prevState => ({
            ...prevState,
            business: updatedBusiness
        }));
    }, []);

    const openArchiveModal = useCallback((campaign) => {
        setSelectedCampaign(campaign);
        setArchiveModal(true);
    }, []);

    const closeArchiveModal = useCallback(() => {
        setArchiveModal(false);
        setSelectedCampaign({});
    }, []);

    const triggerCampaignArchive = useCallback(() => {
        archiveCampaign(selectedCampaign.id)
            .then(resp => {
                // Refresh campaigns
                getCampaigns()
                    .then(resp => {
                        if (resp.status === 200 && resp.data) {
                            setCampaigns(resp.data);
                        }
                    });
                // Refresh brand hub data
                getBrandHub()
                    .then(resp => {
                        if (resp.status === 200 && resp.data) {
                            setBusinessData(resp.data);
                        }
                    });
                setArchiveModal(false);
            });
    }, [selectedCampaign.id]);



    useEffect(() => {
        getBrandHub()
            .then(resp => {
                if (resp.status === 200 && resp.data) {
                    setBusinessData(resp.data);
                }
            })
        getBusinessNotifications()
            .then(resp => {
                if (resp.status === 200 && resp.data) {
                    setNotifications(resp.data);
                }
            })
        setCampaignsLoading(true);
        getCampaigns()
            .then(resp => {
                if (resp.status === 200 && resp.data) {
                    setCampaigns(resp.data);
                }
            })
            .finally(() => {
                setCampaignsLoading(false);
            })
        
        // Show review announcement if not seen before
        const hasSeenAnnouncement = localStorage.getItem('reviewAnnouncementSeen');
        if (!hasSeenAnnouncement) {
            // Delay showing the popup slightly for better UX
            setTimeout(() => {
                setShowReviewAnnouncement(true);
            }, 1000);
        }
            }, [navigate]);

    return (
        <View>
            <Header title={"Brand Hub"} />
            <View style={styles.mainContainer}>
                <View style={styles.hubTopContainer}>
                    <View style={styles.leftColumn}>
                        <View style={styles.businessInfoSection}>
                            <BusinessInfoCard 
                                business={businessData.business} 
                                onBusinessUpdate={handleBusinessUpdate}
                            />
                        </View>
                        <View style={styles.notificationsSection}>
                            <NotificationsList
                                notifications={notifications}
                                isLoading={notificationsLoading}
                            />
                        </View>
                    </View>
                    <View style={styles.rightColumn}>
                        <CampaignHub
                            campaigns={businessData.campaigns}
                            isLoading={campaignsLoading}
                            openArchiveModal={openArchiveModal}
                        />
                    </View>
                </View>
                {businessData.content && businessData.content.length > 0 && (
                    <View style={styles.contentSection}>
                        <Text style={styles.contentTitle}>Your Content</Text>
                        <View style={styles.contentPreviewContainer}>
                            {businessData.content && businessData.content.map((image, index) => (
                                <img 
                                    key={index}
                                    className={"content-preview-img"} 
                                    alt="" 
                                    src={"data:" + image.image_type + ";base64," + image.img} 
                                />
                            ))}
                        </View>
                    </View>
                )}
            <View style={styles.bottomSection}>
                <CreatorSpotlight
                    creators={businessData.creators}
                    onAddCreator={openAddToCampaignModal}
                    isLoading={false}
                />
            </View>
            <Modal
                isOpen={addToCampaignModal}
                onRequestClose={closeModal}
                style={styles.modal}
            >
                <View style={{backgroundColor: 'white', borderRadius: 10, padding: 20, height: '80%'}}>
                    <Text style={[styles.sectionText, {marginBottom: 10, alignSelf: 'center'}]}>Adding {addingCreator.first_name} {addingCreator.last_name} to Campaigns</Text>
                    <ScrollView showsVerticalScrollIndicator={true} ref={campaignAddRef}>
                        {campaigns && campaigns.map(campaign => (
                            <CampaignPreview key={campaign.id} alternateBackground={true} campaign={campaign} creatorId={addingCreator.id}/>
                        ))}
                    </ScrollView>
                </View>
            </Modal>
            <Modal
                isOpen={showReviewAnnouncement}
                onRequestClose={closeReviewAnnouncement}
                style={styles.announcementModal}
            >
                <View style={styles.announcementContainer}>
                    <View style={styles.announcementHeader}>
                        <Text style={styles.announcementTitle}>🎉 New Feature Alert!</Text>
                        <Text style={styles.announcementSubtitle}>Creator Reviews Are Here</Text>
                    </View>
                    <View style={styles.announcementBody}>
                        <Text style={styles.announcementDescription}>
                            Now you can review and rate creators after they complete your campaigns! 
                            Rate their responsiveness and content quality, and leave feedback to help 
                            improve future collaborations.
                        </Text>
                        <View style={styles.announcementFeatures}>
                            <Text style={styles.featureItem}>⭐ Rate responsiveness & quality</Text>
                            <Text style={styles.featureItem}>💬 Leave detailed feedback</Text>
                            <Text style={styles.featureItem}>📈 Build creator relationships</Text>
                        </View>
                    </View>
                    <View style={styles.announcementFooter}>
                        <Button 
                            text="Maybe Later" 
                            variant="secondary" 
                            onPress={closeReviewAnnouncement}
                            style={styles.laterButton}
                        />
                        <Button 
                            text="Check It Out!" 
                            onPress={goToCampaigns}
                            style={styles.checkItOutButton}
                        />
                    </View>
                </View>
            </Modal>
            <ArchiveModal
                isOpen={archiveModal}
                onRequestClose={closeArchiveModal}
                item={selectedCampaign}
                itemType="Campaign"
                onConfirm={triggerCampaignArchive}
            />
            </View>
        </View>

    );
}

const styles = StyleSheet.create({
    mainContainer: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: 20,
    },
    contentWrapper: {
        flex: 1,
    },
    bottomSection: {
        // marginTop: 'auto', // Push to bottom
    },
    hubTopContainer: {
        flexDirection: 'row',
        marginTop: 20,
        gap: 20,
        alignItems: 'stretch',
        height: '50vh', // Fixed to 50% of viewport height
    },
    leftColumn: {
        flex: 1,
        maxWidth: '35%',
        flexDirection: 'column',
        alignSelf: 'stretch',
    },
    rightColumn: {
        flex: 1,
        maxWidth: '62%',
        flexDirection: 'column',
        alignSelf: 'stretch',
        minHeight: 0, // Ensures flex child can be constrained
    },
    businessInfoSection: {
        marginBottom: 20,
    },
    notificationsSection: {
        flex: 1,
    },
    contentSection: {
        marginTop: 30,
        marginLeft: '2.5%',
        marginRight: '2.5%',
    },
    contentTitle: {
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 20,   
        fontWeight: 'bold',
        color: '#396de0',
        marginBottom: 15,
    },
    contentPreviewContainer: {
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 15,
        flexDirection: 'row',
        flexWrap: 'wrap',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 3,
    },
    sectionText: {
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 30,
        fontWeight: 'bold',
        color: '#396de0',
    },
    modal: {
        overlay: {
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            alignContent: 'center',
            backgroundColor: 'rgba(57, 109, 224, .5)'
        },
        content: {
            margin: 'auto',
            width: '50%',
            inset: 0,
            background: 'none',
            height: '100%',
            flex: 1,
            border: 'none',
            alignSelf: 'center',
            alignContent: 'center',
        }
    },
    announcementModal: {
        overlay: {
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            alignContent: 'center',
            backgroundColor: 'rgba(57, 109, 224, .8)'
        },
        content: {
            margin: 'auto',
            maxWidth: '500px',
            inset: 0,
            background: 'none',
            height: 'fit-content',
            border: 'none',
            alignSelf: 'center',
            alignContent: 'center',
        }
    },
    announcementContainer: {
        backgroundColor: 'white',
        borderRadius: 15,
        padding: 30,
        alignItems: 'center',
        textAlign: 'center',
    },
    announcementHeader: {
        marginBottom: 20,
        alignItems: 'center',
    },
    announcementTitle: {
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 28,
        fontWeight: 'bold',
        color: '#396de0',
        marginBottom: 5,
    },
    announcementSubtitle: {
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 22,
        fontWeight: '600',
        color: '#737373',
    },
    announcementBody: {
        marginBottom: 25,
        alignItems: 'center',
    },
    announcementDescription: {
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        color: '#737373',
        lineHeight: 22,
        marginBottom: 15,
        textAlign: 'center',
    },
    announcementFeatures: {
        alignItems: 'flex-start',
        paddingLeft: 20,
    },
    featureItem: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: '#396de0',
        marginBottom: 8,
        fontWeight: '500',
    },
    announcementFooter: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        marginTop: 10,
    },
    laterButton: {
        marginRight: 10,
        minWidth: 120,
    },
    checkItOutButton: {
        marginLeft: 10,
        minWidth: 120,
    },
});

export default BrandHub
